import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useAgreement, useFinalizeAgreement, useSubmitPage, type AgreementPayload } from "./api";
import { SignaturePad } from "./SignaturePad";

// Phase 2a rental-agreement FR-3 / FR-6 / FR-7 — multi-page signing UI.
//
// Each of pages 1–8 enforces:
//   * scroll-to-bottom on the scrollable T&C container (FR-3),
//   * all required-section anchors visible for >=800 ms (FR-6).
// Page 9 collects the signature, printed name, signed date, and (if
// the order opted in) the apparent-agent extra initial.
//
// Keyboard accessibility: Left arrow moves to previous page, Right
// arrow/Enter advances after a valid initial.

const SCROLL_TOLERANCE_PX = 50;
const ANCHOR_VISIBLE_MS = 800;

export function AgreementSignerPage(): JSX.Element {
  const { orderId: orderIdParam } = useParams();
  const [search] = useSearchParams();
  const orderId = Number(orderIdParam ?? search.get("order_id"));
  const navigate = useNavigate();

  const query = useAgreement(orderId);

  if (!Number.isFinite(orderId) || orderId <= 0) {
    return (
      <p className="text-sm text-red-600">
        Missing or invalid <code>order_id</code> in the URL.
      </p>
    );
  }
  if (query.isLoading) return <p>Loading agreement…</p>;
  if (query.isError || !query.data) {
    return <p className="text-sm text-red-600">Could not load the rental agreement.</p>;
  }

  return (
    <AgreementSigner
      orderId={orderId}
      payload={query.data}
      onComplete={() => navigate(`/orders/${orderId}/agreement`)}
    />
  );
}

interface AgreementSignerProps {
  orderId: number;
  payload: AgreementPayload;
  onComplete: () => void;
}

export function AgreementSigner({ orderId, payload, onComplete }: AgreementSignerProps): JSX.Element {
  const totalPages = payload.pages_html.length;
  const initialPage = (() => {
    const initialed = new Set(payload.initialed_page_numbers);
    for (let i = 1; i <= 8; i += 1) if (!initialed.has(i)) return i;
    return 9;
  })();

  const [currentPage, setCurrentPage] = useState(initialPage);

  if (payload.completed_at) {
    return (
      <section data-testid="agreement-complete" className="space-y-3">
        <h1 className="text-lg font-semibold">Agreement signed</h1>
        <p className="text-sm text-zinc-600">You signed this agreement at {payload.completed_at}.</p>
        <a className="text-brand-red underline" href={`/api/v1/orders/${orderId}/agreement.pdf`}>
          Download PDF
        </a>
      </section>
    );
  }

  if (currentPage <= 8) {
    return (
      <ContentPageView
        orderId={orderId}
        page={currentPage}
        totalPages={totalPages}
        pageHtml={payload.pages_html[currentPage - 1] ?? ""}
        requiredAnchors={payload.pages_required_anchors[String(currentPage)] ?? []}
        onAdvance={() => setCurrentPage((p) => Math.min(p + 1, 9))}
        onBack={() => setCurrentPage((p) => Math.max(p - 1, 1))}
      />
    );
  }

  return (
    <SignaturePageView
      orderId={orderId}
      pageHtml={payload.pages_html[8] ?? ""}
      apparentAgentRequired={payload.apparent_agent_required}
      onBack={() => setCurrentPage(8)}
      onComplete={onComplete}
    />
  );
}

interface ContentPageProps {
  orderId: number;
  page: number;
  totalPages: number;
  pageHtml: string;
  requiredAnchors: string[];
  onAdvance: () => void;
  onBack: () => void;
}

function ContentPageView({
  orderId,
  page,
  totalPages,
  pageHtml,
  requiredAnchors,
  onAdvance,
  onBack
}: ContentPageProps): JSX.Element {
  const [initials, setInitials] = useState("");
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [visibleAnchors, setVisibleAnchors] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const submit = useSubmitPage(orderId);

  // Reset gating on every page change.
  useEffect(() => {
    setInitials("");
    setScrolledToBottom(false);
    setVisibleAnchors(new Set());
  }, [page]);

  // Scroll-to-bottom check. If the container doesn't overflow (short
  // pages), satisfy immediately.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    function check() {
      const fits = el!.scrollHeight - el!.clientHeight <= SCROLL_TOLERANCE_PX;
      const atBottom = el!.scrollTop + el!.clientHeight >= el!.scrollHeight - SCROLL_TOLERANCE_PX;
      if (fits || atBottom) setScrolledToBottom(true);
    }
    check();
    el.addEventListener("scroll", check);
    return () => el.removeEventListener("scroll", check);
  }, [page, pageHtml]);

  // IntersectionObserver — anchor must be visible for >= 800 ms.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || requiredAnchors.length === 0) return;
    const dwellTimers = new Map<string, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id;
          if (entry.isIntersecting) {
            const t = window.setTimeout(() => {
              setVisibleAnchors((prev) => {
                if (prev.has(id)) return prev;
                const next = new Set(prev);
                next.add(id);
                return next;
              });
            }, ANCHOR_VISIBLE_MS);
            dwellTimers.set(id, t);
          } else {
            const t = dwellTimers.get(id);
            if (t) {
              window.clearTimeout(t);
              dwellTimers.delete(id);
            }
          }
        });
      },
      { root: el, threshold: 0.4 }
    );
    requiredAnchors.forEach((id) => {
      const target = el.querySelector(`#${CSS.escape(id)}`);
      if (target) observer.observe(target);
    });
    return () => {
      observer.disconnect();
      dwellTimers.forEach((t) => window.clearTimeout(t));
    };
  }, [page, pageHtml, requiredAnchors]);

  const anchorsSatisfied = requiredAnchors.every((id) => visibleAnchors.has(id));
  const inputEnabled = scrolledToBottom && anchorsSatisfied;
  const canContinue = inputEnabled && initials.trim().length >= 2;

  function handleSubmit() {
    if (!canContinue || submit.isPending) return;
    submit.mutate(
      { order_id: orderId, page_number: page, initials: initials.trim() },
      { onSuccess: () => onAdvance() }
    );
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === "ArrowRight") {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      onBack();
    }
  }

  return (
    <section className="space-y-4" data-testid={`agreement-page-${page}`}>
      <header className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">
          Rental agreement — Page {page} of {totalPages}
        </h1>
      </header>

      <div
        ref={scrollRef}
        data-testid={`agreement-scroll-${page}`}
        tabIndex={0}
        className="h-[480px] overflow-y-auto rounded border border-zinc-200 bg-white p-6 text-sm leading-6"
        dangerouslySetInnerHTML={{ __html: pageHtml }}
      />

      <div className="space-y-2">
        <label htmlFor={`initials-${page}`} className="block text-sm font-medium">
          Type your initials to acknowledge this page
        </label>
        <input
          id={`initials-${page}`}
          type="text"
          aria-label={`Initials for page ${page} of ${totalPages}`}
          value={initials}
          onChange={(e) => setInitials(e.target.value)}
          onKeyDown={handleKey}
          disabled={!inputEnabled}
          className="w-32 rounded border border-zinc-300 px-3 py-2 disabled:bg-zinc-100"
        />
        {!inputEnabled && (
          <p className="text-xs text-zinc-500" data-testid="agreement-gate-hint">
            {scrolledToBottom
              ? "Scroll the highlighted sections into view to enable signing."
              : "Scroll to the bottom of this page to enable signing."}
          </p>
        )}
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          className="rounded border border-zinc-300 px-3 py-1 text-sm"
          onClick={onBack}
          disabled={page === 1}
        >
          Back
        </button>
        <button
          type="button"
          data-testid="continue-button"
          className="rounded bg-brand-red px-3 py-1 text-sm text-white disabled:opacity-50"
          onClick={handleSubmit}
          disabled={!canContinue || submit.isPending}
        >
          Initial and continue
        </button>
      </div>
    </section>
  );
}

interface SignaturePageProps {
  orderId: number;
  pageHtml: string;
  apparentAgentRequired: boolean;
  onBack: () => void;
  onComplete: () => void;
}

function SignaturePageView({
  orderId,
  pageHtml,
  apparentAgentRequired,
  onBack,
  onComplete
}: SignaturePageProps): JSX.Element {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [signature, setSignature] = useState<{ dataUrl: string; type: "drawn" | "typed" }>({
    dataUrl: "",
    type: "drawn"
  });
  const [printedName, setPrintedName] = useState("");
  const [signedDate, setSignedDate] = useState(today);
  const [apparentInitials, setApparentInitials] = useState("");
  const finalize = useFinalizeAgreement(orderId);

  const canFinalize =
    signature.dataUrl.length > 0 &&
    printedName.trim().length > 0 &&
    signedDate.length > 0 &&
    (!apparentAgentRequired || apparentInitials.trim().length >= 2);

  const onSubmit = useCallback(() => {
    if (!canFinalize || finalize.isPending) return;
    const body: import("./api").FinalizeBody = {
      order_id: orderId,
      signature_data_url: signature.dataUrl,
      signature_type: signature.type,
      printed_name: printedName.trim(),
      signed_date: signedDate
    };
    if (apparentAgentRequired) body.apparent_agent_initials = apparentInitials.trim();
    finalize.mutate(body, { onSuccess: () => onComplete() });
  }, [
    canFinalize,
    finalize,
    orderId,
    signature,
    printedName,
    signedDate,
    apparentInitials,
    apparentAgentRequired,
    onComplete
  ]);

  return (
    <section className="space-y-4" data-testid="agreement-page-9">
      <header className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Rental agreement — Page 9 of 9 (signature)</h1>
      </header>

      <div
        className="rounded border border-zinc-200 bg-white p-6 text-sm leading-6"
        dangerouslySetInnerHTML={{ __html: pageHtml }}
      />

      <div className="space-y-4">
        <SignaturePad onChange={setSignature} initialName={printedName} />

        <div>
          <label htmlFor="printed-name" className="block text-sm font-medium">
            Printed name
          </label>
          <input
            id="printed-name"
            type="text"
            value={printedName}
            onChange={(e) => setPrintedName(e.target.value)}
            className="mt-1 w-full max-w-md rounded border border-zinc-300 px-3 py-2"
          />
        </div>

        <div>
          <label htmlFor="signed-date" className="block text-sm font-medium">
            Date
          </label>
          <input
            id="signed-date"
            type="date"
            value={signedDate}
            onChange={(e) => setSignedDate(e.target.value)}
            className="mt-1 rounded border border-zinc-300 px-3 py-2"
          />
        </div>

        {apparentAgentRequired && (
          <div className="rounded border border-amber-300 bg-amber-50 p-4 text-sm">
            <label htmlFor="apparent-initials" className="block font-medium">
              Initial here to authorize JDR to leave the equipment at the site address without a
              signature if no one is present (contract §3d).
            </label>
            <input
              id="apparent-initials"
              type="text"
              value={apparentInitials}
              onChange={(e) => setApparentInitials(e.target.value)}
              className="mt-2 w-32 rounded border border-zinc-300 px-3 py-2"
              aria-label="Apparent agent initials"
            />
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          className="rounded border border-zinc-300 px-3 py-1 text-sm"
          onClick={onBack}
        >
          Back
        </button>
        <button
          type="button"
          data-testid="sign-and-complete"
          className="rounded bg-brand-red px-3 py-1 text-sm text-white disabled:opacity-50"
          onClick={onSubmit}
          disabled={!canFinalize || finalize.isPending}
        >
          Sign and complete
        </button>
      </div>
    </section>
  );
}
